import { DragDropProvider, type DragEndEvent } from "@dnd-kit/react";
import { move } from "@dnd-kit/helpers";
import { useSortable } from "@dnd-kit/react/sortable";
import { ChevronDown, ChevronRight, GripVertical, Pencil, Trash2 } from "lucide-react";
import { Fragment, useCallback, useState } from "react";
import { Button } from "../../../components/button";
import CommonPagination from "../../../components/common/CommonPagination";
import {
  DataTable,
  type DataTableColumn,
  type DataTablePagination,
} from "../../../components/table/data-table";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../../components/table";
import type { MerchantCategory } from "../types/merchantCategory.types";

type MerchantCategoriesTableProps = {
  categories: MerchantCategory[];
  loading: boolean;
  error: string | null;
  onRetry: () => void;
  pagination: DataTablePagination;
  onEdit: (category: MerchantCategory) => void;
  onDelete: (category: MerchantCategory) => void;
  onReorder: (orderedIds: string[]) => Promise<void>;
};

type SortableParentRowProps = {
  category: MerchantCategory;
  index: number;
  isExpanded: boolean;
  onToggle: () => void;
  renderActions: (category: MerchantCategory) => React.ReactNode;
};

const isSvgUrl = (url: string) => /\.svg(?:[?#]|$)|^data:image\/svg\+xml/i.test(url);

function IconPreview({ url, label }: { url: string | null; label: string }) {
  if (!url) return <span className="text-gray-400">—</span>;

  if (isSvgUrl(url)) {
    return (
      <object
        data={url}
        type="image/svg+xml"
        aria-label={`${label} preview`}
        className="pointer-events-none h-8 w-8 rounded border border-gray-200 object-contain"
      >
        <span className="text-gray-400">—</span>
      </object>
    );
  }

  return (
    <img
      src={url}
      alt={`${label} preview`}
      className="h-8 w-8 rounded border border-gray-200 object-cover"
    />
  );
}

function SortableParentRow({
  category,
  index,
  isExpanded,
  onToggle,
  renderActions,
}: SortableParentRowProps) {
  const { ref, handleRef } = useSortable({ id: category.id, index });

  return (
    <TableRow
      ref={ref}
      className="cursor-pointer"
      onClick={() => {
        if (category.children.length > 0) onToggle();
      }}
    >
      <TableCell className="w-12 p-3">
        <button
          ref={handleRef}
          type="button"
          className="cursor-grab text-gray-400 hover:text-gray-700 active:cursor-grabbing"
          aria-label={`Reorder ${category.description}`}
          onClick={(event) => event.stopPropagation()}
        >
          <GripVertical className="h-4 w-4" />
        </button>
      </TableCell>
      <TableCell className="w-12 p-3">
        {category.children.length > 0 ? (
          isExpanded ? (
            <ChevronDown className="h-4 w-4" />
          ) : (
            <ChevronRight className="h-4 w-4" />
          )
        ) : null}
      </TableCell>
      <TableCell className="p-3 font-medium">{category.description}</TableCell>
      <TableCell className="p-3">{category.identifier ?? "—"}</TableCell>
      <TableCell className="p-3">
        <IconPreview
          url={category.icon_url}
          label={`${category.description} icon`}
        />
      </TableCell>
      <TableCell className="p-3">
        <IconPreview
          url={category.icon_hover_url}
          label={`${category.description} hover icon`}
        />
      </TableCell>
      <TableCell className="p-3">{category.order_number}</TableCell>
      <TableCell className="p-3">{category.children.length}</TableCell>
      <TableCell className="p-3 text-right">{renderActions(category)}</TableCell>
    </TableRow>
  );
}

export function MerchantCategoriesTable({
  categories,
  loading,
  error,
  onRetry,
  pagination,
  onEdit,
  onDelete,
  onReorder,
}: MerchantCategoriesTableProps) {
  const [expandedCategoryId, setExpandedCategoryId] = useState<string | null>(null);

  const renderActions = (category: MerchantCategory) => (
    <div className="flex justify-end gap-2">
      <Button
        variant="outline"
        size="sm"
        aria-label={`Edit ${category.description}`}
        onClick={(event) => {
          event.stopPropagation();
          onEdit(category);
        }}
      >
        <Pencil className="h-4 w-4" />
      </Button>
      <Button
        variant="outline"
        size="sm"
        aria-label={`Delete ${category.description}`}
        onClick={(event) => {
          event.stopPropagation();
          onDelete(category);
        }}
      >
        <Trash2 className="h-4 w-4 text-[#EC5454]" />
      </Button>
    </div>
  );

  const childColumns: DataTableColumn<MerchantCategory>[] = [
    {
      key: "description",
      title: "Child category",
      minWidth: 200,
      render: (category) => <span className="font-medium">{category.description}</span>,
    },
    {
      key: "identifier",
      title: "Identifier",
      minWidth: 160,
      render: (category) => category.identifier ?? "—",
    },
    {
      key: "icon",
      title: "Icon",
      minWidth: 72,
      render: (category) => (
        <IconPreview
          url={category.icon_url}
          label={`${category.description} icon`}
        />
      ),
    },
    {
      key: "hover-icon",
      title: "Hover icon",
      minWidth: 104,
      render: (category) => (
        <IconPreview
          url={category.icon_hover_url}
          label={`${category.description} hover icon`}
        />
      ),
    },
    { key: "order", title: "Order", minWidth: 90, render: (category) => category.order_number },
    {
      key: "actions",
      title: "Actions",
      align: "right",
      minWidth: 140,
      render: renderActions,
    },
  ];

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      if (event.canceled) return;

      const sortableCategories = categories.map((category) => ({ id: category.id, category }));
      const reorderedCategories = move(sortableCategories, event).map(({ category }) => category);
      const orderedIds = reorderedCategories.map((category) => category.id);
      if (orderedIds.every((id, index) => id === categories[index]?.id)) return;

      void onReorder(orderedIds);
    },
    [categories, onReorder],
  );

  if (loading || error) {
    return (
      <DataTable
        columns={[]}
        dataSource={[]}
        rowKey="id"
        loading={loading}
        error={error}
        onRetry={onRetry}
        emptyLabel="No merchant categories found."
      />
    );
  }

  return (
    <>
      <DragDropProvider onDragEnd={handleDragEnd}>
        <Table containerClassName="overflow-auto table-scrollbar">
          <TableHeader>
            <TableRow>
              <TableHead className="w-12 p-3" />
              <TableHead className="w-12 p-3" />
              <TableHead className="min-w-[200px] p-3">Description</TableHead>
              <TableHead className="min-w-[160px] p-3">Identifier</TableHead>
              <TableHead className="min-w-[72px] p-3">Icon</TableHead>
              <TableHead className="min-w-[104px] p-3">Hover icon</TableHead>
              <TableHead className="min-w-[90px] p-3">Order</TableHead>
              <TableHead className="min-w-[100px] p-3">Children</TableHead>
              <TableHead className="min-w-[140px] p-3 text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {categories.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={9}
                  className="p-8 text-center text-gray-500"
                >
                  No merchant categories found.
                </TableCell>
              </TableRow>
            ) : (
              categories.map((category, index) => (
                <Fragment key={category.id}>
                  <SortableParentRow
                    category={category}
                    index={index}
                    isExpanded={expandedCategoryId === category.id}
                    onToggle={() =>
                      setExpandedCategoryId((current) =>
                        current === category.id ? null : category.id,
                      )
                    }
                    renderActions={renderActions}
                  />
                  {expandedCategoryId === category.id && (
                    <TableRow className="hover:bg-transparent">
                      <TableCell
                        colSpan={9}
                        className="bg-gray-50/70 p-4 dark:bg-gray-800/50"
                      >
                        <h2 className="mb-4 text-base font-semibold text-[#1D1D1F]">
                          {category.description} child categories
                        </h2>
                        <DataTable
                          columns={childColumns}
                          dataSource={category.children}
                          rowKey="id"
                          emptyLabel="No child categories found."
                        />
                      </TableCell>
                    </TableRow>
                  )}
                </Fragment>
              ))
            )}
          </TableBody>
        </Table>
      </DragDropProvider>
      <CommonPagination
        page={pagination.page}
        totalPages={pagination.totalPages}
        onPageChange={pagination.onPageChange}
      />
    </>
  );
}
